import React from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Typography,
  Chip,
  TablePagination,
  Drawer,
  IconButton
} from '@mui/material';
import {
  Devices as DevicesIcon,
  AccountTree as ChildrenIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { useIsMobile } from '../../../../hooks/useResponsive';
import LocationTypeIcon from '../shared/LocationTypeIcon';
import { CardGridSkeleton } from '../shared/LocationSkeleton';
import LocationEmptyState from '../shared/LocationEmptyState';
import LocationDetailPanel from '../detail/LocationDetailPanel';
import {
  getLocationId,
  getLocationName,
  getLocationTypeId,
  getLocationTypeName,
  computeBreadcrumbPath
} from '../../utils/locationTransformers';
import { getLocationTypeConfig, DETAIL_PANEL_SX, MOBILE_DRAWER_PAPER_SX } from '../../utils/locationConstants';

/**
 * Card Grid View - Visual card layout for locations
 * 2 columns on mobile, 3-4 on desktop
 */
const CardGridView = ({
  flatLocations,
  filteredLocations,
  paginatedLocations,
  totalCount,
  loading,
  error,
  selectedLocation,
  page,
  rowsPerPage,
  onChangePage,
  onChangeRowsPerPage,
  onLocationSelect,
  onRetry,
  detailPanelRef,
  detailPanelProps,
  t
}) => {
  const isMobile = useIsMobile();

  if (loading) {
    return <CardGridSkeleton count={8} />;
  }

  if (error) {
    return <LocationEmptyState type="error" errorMessage={error} onAction={onRetry} t={t} />;
  }

  if (filteredLocations.length === 0) {
    return <LocationEmptyState type="no_results" t={t} />;
  }

  return (
    <Box sx={{
      display: 'flex',
      flex: 1,
      overflow: 'hidden',
      height: { xs: 'auto', md: 'calc(100vh - 230px)' },
      flexDirection: { xs: 'column', md: 'row' }
    }}>
      {/* Cards area */}
      <Box sx={{ flex: 1, overflow: 'auto', p: { xs: 1, sm: 2 } }}>
        <Grid container spacing={{ xs: 1, sm: 2 }}>
          {paginatedLocations.map((location) => {
            const locId = getLocationId(location);
            const typeId = getLocationTypeId(location);
            const config = getLocationTypeConfig(typeId);
            const isSelected = selectedLocation && String(getLocationId(selectedLocation)) === String(locId);
            const breadcrumb = computeBreadcrumbPath(flatLocations, locId);
            const childCount = location.children?.length || 0;

            return (
              <Grid item xs={6} sm={6} md={4} lg={3} key={locId}>
                <Card
                  variant={isSelected ? 'elevation' : 'outlined'}
                  elevation={isSelected ? 4 : 0}
                  sx={{
                    borderColor: isSelected ? config.color : 'divider',
                    borderWidth: isSelected ? 2 : 1,
                    transition: 'all 0.2s',
                    '&:hover': { borderColor: config.color }
                  }}
                >
                  <CardActionArea onClick={() => onLocationSelect(location)}>
                    <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <LocationTypeIcon typeId={typeId} size={isMobile ? 20 : 24} withBackground />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            variant={isMobile ? 'body2' : 'subtitle1'}
                            fontWeight={600}
                            noWrap
                          >
                            {getLocationName(location)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {getLocationTypeName(location)}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Parent breadcrumb - hidden on mobile */}
                      {!isMobile && breadcrumb.length > 1 && (
                        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', mb: 1 }}>
                          {breadcrumb.slice(0, -1).map(c => c.name).join(' > ')}
                        </Typography>
                      )}

                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {(location.deviceCount > 0) && (
                          <Chip
                            icon={<DevicesIcon />}
                            label={location.deviceCount}
                            size="small"
                            variant="outlined"
                            sx={{ height: isMobile ? 20 : 24, '& .MuiChip-label': { fontSize: isMobile ? '0.7rem' : undefined } }}
                          />
                        )}
                        {childCount > 0 && (
                          <Chip
                            icon={<ChildrenIcon />}
                            label={childCount}
                            size="small"
                            variant="outlined"
                            sx={{ height: isMobile ? 20 : 24, '& .MuiChip-label': { fontSize: isMobile ? '0.7rem' : undefined } }}
                          />
                        )}
                      </Box>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            );
          })}
        </Grid>

        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={onChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={onChangeRowsPerPage}
          rowsPerPageOptions={isMobile ? [12, 24] : [12, 24, 48, { label: 'All', value: -1 }]}
          labelRowsPerPage={isMobile ? '' : 'Per page:'}
          sx={isMobile ? { '.MuiTablePagination-selectLabel': { display: 'none' } } : {}}
        />
      </Box>

      {/* Desktop detail panel */}
      {selectedLocation && !isMobile && (
        <Box ref={detailPanelRef} sx={DETAIL_PANEL_SX}>
          <LocationDetailPanel
            selectedLocation={selectedLocation}
            {...detailPanelProps}
          />
        </Box>
      )}

      {/* Mobile detail drawer */}
      {selectedLocation && isMobile && (
        <Drawer
          anchor="bottom"
          open={!!selectedLocation}
          onClose={() => onLocationSelect(null)}
          PaperProps={{ sx: MOBILE_DRAWER_PAPER_SX }}
        >
          <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}>
            <IconButton
              onClick={() => onLocationSelect(null)}
              size="small"
              aria-label="Close detail panel"
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <LocationDetailPanel
            selectedLocation={selectedLocation}
            {...detailPanelProps}
          />
        </Drawer>
      )}
    </Box>
  );
};

export default CardGridView;
